/**
 * Spelling Werkwoorden (SWW) Support Registry
 *
 * Support content (hints, explanations, visuals) for SWW families.
 *
 * Module: SWW (Spelling Werkwoorden)
 * Families: 24
 * Coverage: 100% (migrated: 2026-03-25)
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 */

import { FamilySupportEntry } from '../familySupportRegistry';

/**
 * SWW Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const SWW_SUPPORT: Record<string, FamilySupportEntry> = {
  'SWW-TT-STAM': {
    familyId: 'SWW-TT-STAM',
    bubbleHint: 'Stam zoeken? Haal de "-en" eraf — dat wat overblijft is meestal de stam.',
    defaultExplanation: "De stam is wat je overhoudt als je '-en' van het werkwoord afhaalt. Voorbeeld: 'lopen' → haal '-en' weg → stam = 'loop'.",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Zo doe je het',
      content: "Haal '-en' van het hele werkwoord af. Wat overblijft is de stam.\n\nlopen → loop\nmaken → maak\nvinden → vind\nspelen → speel"
    }, {
      title: 'Soms verdubbel je een letter',
      content: "Bij sommige werkwoorden plak je er een extra klinker bij, zodat het goed klinkt:\n\nlopen → lop → loop (extra 'o')\nspelen → spel → speel (extra 'e')\n\nTrucje: zeg het hardop. Klinkt 'lop' raar? Dan moet er een letter bij."
    }]
  },
  'SWW-TT-INV-JIJ': {
    familyId: 'SWW-TT-INV-JIJ',
    bubbleHint: "Staat 'jij' ACHTER het werkwoord? Dan valt de -t weg. 'Loop jij?' — geen t.",
    defaultExplanation: "Als 'jij' of 'je' achter het werkwoord komt, schrijf je geen -t.\nVoorbeeld: 'Jij loopt' → 'Loop jij?' (geen t).",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De regel',
      content: "Staat 'jij' of 'je' ACHTER het werkwoord? → schrijf alleen de stam, GEEN t.\n\n'Loop jij naar school?'\n'Werk je vandaag?'\n'Vind jij dat ook?'"
    }, {
      title: 'Voor of achter? Kijk even',
      content: "Jij loopt.   → jij staat ervoor → +t\nLoop jij?    → jij staat erachter → geen t\n\nSnelle check: vraag jezelf 'staat jij voor of achter het werkwoord?'"
    }]
  },
  'SWW-VT-STERK': {
    familyId: 'SWW-VT-STERK',
    defaultExplanation: 'Sterke werkwoorden veranderen van klank in de verleden tijd: lopen → liep, schrijven → schreef. Ze krijgen GEEN -te of -de. Deze vormen moet je uit je hoofd leren.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Wat zijn sterke werkwoorden?',
      content: 'Bij sterke werkwoorden verandert de klinker:\nlopen → liep\nzingen → zong\nhelpen → hielp\nschrijven → schreef'
    }, {
      title: 'Geen -te of -de!',
      content: 'Sterke werkwoorden krijgen GEEN -te of -de.\nNIET: loopte, zingde\nWEL: liep, zong'
    }]
  },
  'SWW-VT-ZWAK-KOF': {
    familyId: 'SWW-VT-ZWAK-KOF',
    bubbleHint: "'t kofschip-truc: eindigt de stam op t/k/f/s/ch/p? → -te. Anders → -de.",
    defaultExplanation: "Verleden tijd met -te of -de? Gebruik 't kofschip. Eindigt de stam op t, k, f, s, ch of p? Dan +te. Eindigt hij op iets anders? Dan +de.",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: "'t kofschip onthouden",
      content: "Onthoud de letters: 't k o f s ch p\n\nKijk naar de laatste letter van de stam.\nZit hij in 't kofschip? → +te(n)\nZit hij er NIET in? → +de(n)\n\nVoorbeelden met +te:\nwerk → werkte (k)\nfiets → fietste (s)\nblaf → blafte (f)"
    }, {
      title: 'Niet in kofschip? → -de(n)',
      content: "Eindigt de stam op een andere letter?\n→ stam + de(n)\n\nleer → leerde (r)\nspeel → speelden (l)\nrij → reden\n\nLet op: bij 'leven' wordt v een f → leefde. Sluip-truc!"
    }, {
      title: 'Uitzonderingen & sterke werkwoorden',
      content: "Pas op voor:\n• Sterke werkwoorden: lopen → liep (NIET loopte!)\n  Klankverandering = sterk = geen kofschip\n• V/F-wissel: verven → verfde (klinkt beter dan verfte)\n• Signaalwoorden: 'gisteren', 'vorige week' = verleden tijd\n• Geen hulpwerkwoord? Dan geen voltooid deelwoord!"
    }]
  },
  'SWW-VD-KOF': {
    familyId: 'SWW-VD-KOF',
    defaultExplanation: 'Het voltooid deelwoord: ge + stam + t (bij kofschip) of ge + stam + d (geen kofschip). Voorbeeld: werken → gewerkt, leven → geleefd.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      content: "Stam eindigt op 't kofschip letter?\n→ ge + stam + t\n\nVoorbeelden:\nwerk → gewerkt (K)\ndans → gedanst (S)\npost → gepost (T, al een t dus geen extra)"
    }]
  },
  'SWW-TT-DSTAM': {
    familyId: 'SWW-TT-DSTAM',
    defaultExplanation: 'Als de stam eindigt op -d, krijg je bij hij/zij: d + t = dt. Voorbeeld: hij vindt (stam = vind). Bij "ik" schrijf je alleen de stam: ik vind.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Regel',
      content: 'Eindigt de stam op een d? Dan schrijf je bij hij/zij: d + t = dt.\nhij vindt (stam = vind)\nhij wordt (stam = word)\nhij verzendt (stam = verzend)'
    }, {
      title: 'Ik-vorm: geen t!',
      content: 'Bij "ik" alleen de stam:\nik vind (niet: ik vindt)\nik word (niet: ik wordt)'
    }]
  },
  'SWW-VT-VZFS': {
    familyId: 'SWW-VT-VZFS',
    defaultExplanation: 'Let op de v→f en z→s wissel! Bij "verven" is de stam "verf" (f zit in kofschip → verfde). Kijk altijd naar het hele werkwoord, niet alleen de stam.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'V/F-wissel',
      content: 'Stam is verf, maar het werkwoord is verVen.\nKijk naar de V (niet in kofschip) → verfde\n\nRegel: kijk altijd naar het hele werkwoord!'
    }, {
      title: 'Z/S-wissel',
      content: 'Stam is verhuis, maar het werkwoord is verhuiZen.\nZ niet in kofschip → verhuisde'
    }]
  },
  'SWW-VT-DUBBEL': {
    familyId: 'SWW-VT-DUBBEL',
    defaultExplanation: 'Als de stam al eindigt op -d of -t, krijg je dubbele letters: redden → redde, antwoorden → antwoordde, wachten → wachtte.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Dubbele letters',
      content: 'Stam op d + de = dubbel d: red → redde\nStam op t + te = dubbel t: wacht → wachtte'
    }]
  },
  'SWW-BN-VD-KORT': {
    familyId: 'SWW-BN-VD-KORT',
    bubbleHint: "Beschrijft het werkwoord het zelfstandig naamwoord? Plak er '-e' achter.",
    defaultExplanation: "Als een voltooid deelwoord een zelfstandig naamwoord beschrijft (zoals 'de verbrande auto'), plak je er '-e' achter.",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: plak er -e achter',
      content: "Beschrijft het werkwoord een ding of persoon? Dan plak je '-e' achter het voltooid deelwoord.\n\nDe auto is verbrand. → de verbrande auto\nDe muur is geverfd. → de geverfde muur\nHet huis is verbouwd. → het verbouwde huis"
    }, {
      title: 'Hoe weet je dat?',
      content: "Zeg het in jezelf:\n• 'de ___ auto' — past het ervoor? Dan beschrijft het de auto.\n• Past het? → +e\n\nKijk:\n  verbrand → de verbrande auto\n  gemaakt → het gemaakte werkstuk"
    }]
  },
  'SWW-TT-INV-TRICK': {
    familyId: 'SWW-TT-INV-TRICK',
    bubbleHint: "Trucvraag! De -t valt alléén weg bij 'jij/je'. Bij 'hij' blijft hij staan.",
    defaultExplanation: "De -t-weglaattruc werkt alléén voor 'jij' en 'je'. Voor 'hij', 'zij' of 'het' blijft de -t gewoon staan: 'Loopt hij?'",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: "Alléén bij 'jij' of 'je'",
      content: "Staat 'jij' of 'je' achter het werkwoord? → geen t.\n'Loop jij?' / 'Werk je?'\n\nMaar bij 'hij', 'zij', 'het' → de t blijft!\n'Loopt hij?'\n'Werkt zij?'"
    }, {
      title: 'Snelle check',
      content: "Kijk wie er ACHTER het werkwoord staat:\n• jij / je  → geen t\n• hij / zij / het / iemand anders → +t\n\nVoorbeeld:\n'Komt hij ook?' (hij → +t)\n'Kom jij ook?' (jij → geen t)"
    }]
  },
  'SWW-BN-VD-DUBBEL': {
    familyId: 'SWW-BN-VD-DUBBEL',
    bubbleHint: 'Korte klank? Verdubbel de laatste letter. Lange klank? Gewoon één.',
    defaultExplanation: "Hoor je een KORTE klank (zoals in 'pet')? Verdubbel de laatste medeklinker: gered → geredde. Bij een LANGE klank (zoals in 'peet') niet: gepoot → gepote.",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Korte klank → dubbele letter',
      content: "Klinkt het kort (zoals 'pet', 'pot', 'pit')?\n→ Verdubbel de medeklinker als je '-e' plakt:\n\ngered → de geredde jongen\ngestopt → de gestopte auto"
    }, {
      title: 'Lange klank → één letter',
      content: "Klinkt het lang (zoals 'peet', 'poot', 'piet')?\n→ Géén verdubbeling:\n\ngepoot → de gepote bloem\nbesteed → het bestede geld\n\nSnelle check: zeg het hardop. Klinkt het kort? Dubbel. Klinkt het lang? Eén."
    }]
  },
  'SWW-VD-HOMOFOON': {
    familyId: 'SWW-VD-HOMOFOON',
    bubbleHint: "Twijfel over 'word' of 'wordt'? Doe de smurfen-truc: vervang door 'smurf(t)'.",
    defaultExplanation: "Twijfel je tussen 'word' en 'wordt'? Vervang het werkwoord door 'smurfen' en kijk wat goed klinkt: 'smurf' → geen t, 'smurft' → wel t.",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De smurfen-truc',
      content: "Vervang het werkwoord door 'smurfen' en luister wat goed klinkt:\n\n'Ik word/wordt boos.'\n→ 'Ik smurf/smurft boos.' → 'smurf' klinkt goed → IK WORD\n\n'Hij word/wordt boos.'\n→ 'Hij smurf/smurft' → 'smurft' klinkt goed → HIJ WORDT"
    }, {
      title: "'Leiden' of 'lijden'?",
      content: "Leiden (ei) = de weg wijzen / leiden als baas.\n   → 'Hij leidt de groep.'\n\nLijden (ij) = pijn hebben / iets vervelends meemaken.\n   → 'Hij lijdt aan hoofdpijn.'\n\nEzelsbruggetje: lIJden doet pIJn."
    }]
  },
  'SWW-LEEN-ENG-STAM': {
    familyId: 'SWW-LEEN-ENG-STAM',
    defaultExplanation: 'Bij Engelse leenwoorden bepaal je de stam op dezelfde manier: het hele werkwoord min -en. Let op de klank, niet de spelling: racen → race.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'De stam = het hele werkwoord min -en.\nlopen → loop\nmaken → maak\nvinden → vind\nspelen → speel'
    }]
  },
  'SWW-LEEN-ENG-KOF': {
    familyId: 'SWW-LEEN-ENG-KOF',
    defaultExplanation: "Bij Engelse leenwoorden pas je 't kofschip toe op de KLANK van de stam. Race (klank s, zit in kofschip) → racete. Download (klank d, niet in kofschip) → downloadde.",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      content: "De letters van 't Kofschip: T, K, F, S, CH, P\n\nEindigt de stam op één van deze letters?\n→ Verleden tijd: stam + te (enkelvoud) of + ten (meervoud)\n\nVoorbeelden:\nwerk → werkte/werkten (K)\nfiets → fietste/fietsten (S)\nblaf → blafte (F)"
    }]
  },
  'SWW-TT-ZIJN-HEBBEN': {
    familyId: 'SWW-TT-ZIJN-HEBBEN',
    defaultExplanation: 'Zijn en hebben zijn onregelmatige werkwoorden. Je moet de vormen uit je hoofd leren: ik ben, jij bent, hij is, wij zijn. Ik heb, jij hebt, hij heeft, wij hebben.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Zijn — tegenwoordige tijd',
      content: 'ik ben\njij bent / ben jij? (inversie: geen t)\nhij/zij is\nwij/jullie/zij zijn'
    }, {
      title: 'Hebben — tegenwoordige tijd',
      content: 'ik heb\njij hebt / heb jij? (inversie: geen t)\nhij/zij heeft\nwij/jullie/zij hebben'
    }]
  },
  'SWW-TT-MEERVOUD': {
    familyId: 'SWW-TT-MEERVOUD',
    defaultExplanation: 'Bij meervoud (wij, jullie, zij) gebruik je altijd het hele werkwoord: wij lopen, jullie fietsen, zij werken. Geen -t toevoegen!',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Regel',
      content: 'Meervoud = het hele werkwoord:\nwij lopen (niet: wij loopt)\njullie fietsen (niet: jullie fietst)\nzij werken (niet: zij werkt)'
    }, {
      title: 'Let op!',
      content: 'Alleen bij hij/zij enkelvoud voeg je -t toe.\nBij wij/jullie/zij meervoud NOOIT een -t.'
    }]
  },
  'SWW-TT-GEBIEDENDE': {
    familyId: 'SWW-TT-GEBIEDENDE',
    defaultExplanation: 'De gebiedende wijs is een bevel of verzoek. Je gebruikt alleen de stam: "Loop!", "Kom hier!", "Ga zitten!"',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Regel',
      content: 'Gebiedende wijs = alleen de stam:\nLopen → Loop!\nKomen → Kom!\nZitten → Zit!\n\nGeen -t, geen -en, alleen de stam.'
    }, {
      title: 'Beleefd',
      content: 'Beleefde gebiedende wijs met u:\n"Gaat u zitten" (stam + t)\n"Komt u binnen"'
    }]
  },
  'SWW-VT-ZIJN-HEBBEN': {
    familyId: 'SWW-VT-ZIJN-HEBBEN',
    defaultExplanation: 'Zijn en hebben in de verleden tijd: ik was, jij was, hij was, wij waren. Ik had, jij had, hij had, wij hadden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Zijn — verleden tijd',
      content: 'ik was\njij was\nhij/zij was\nwij/jullie/zij waren'
    }, {
      title: 'Hebben — verleden tijd',
      content: 'ik had\njij had\nhij/zij had\nwij/jullie/zij hadden'
    }]
  },
  'SWW-VT-ONREGELMATIG': {
    familyId: 'SWW-VT-ONREGELMATIG',
    defaultExplanation: 'Onregelmatige werkwoorden volgen geen vast patroon. De verleden tijd moet je uit je hoofd leren: doen → deed, gaan → ging, staan → stond.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Veelvoorkomende onregelmatige werkwoorden',
      content: 'doen → deed/deden\ngaan → ging/gingen\nstaan → stond/stonden\nzien → zag/zagen\nkomen → kwam/kwamen'
    }, {
      title: 'Tip',
      content: 'Onregelmatige werkwoorden herken je doordat de klinker verandert EN ze niet in het kofschip-patroon passen.\nLeer ze per groep!'
    }]
  },
  'SWW-VT-ZULLEN-KUNNEN': {
    familyId: 'SWW-VT-ZULLEN-KUNNEN',
    defaultExplanation: 'Hulpwerkwoorden in de verleden tijd: zullen → zou/zouden, kunnen → kon/konden, willen → wilde/wilden, mogen → mocht/mochten.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Hulpwerkwoorden verleden tijd',
      content: 'zullen → zou (ek) / zouden (mv)\nkunnen → kon / konden\nwillen → wilde / wilden (of: wou/wouden)\nmogen → mocht / mochten'
    }, {
      title: 'Gebruik',
      content: 'Zou + infinitief = beleefd verzoek of voorwaarde:\n"Zou je me willen helpen?"\n"Als ik kon, zou ik komen."'
    }]
  },
  'SWW-VD-STERK': {
    familyId: 'SWW-VD-STERK',
    defaultExplanation: 'Het voltooid deelwoord van sterke werkwoorden eindigt op -en: gelopen, geschreven, gezwommen. De klinker verandert vaak.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Sterke VD',
      content: 'Sterke werkwoorden krijgen ge- ... -en:\nlopen → gelopen\nschrijven → geschreven\nzwemmen → gezwommen\nrijden → gereden'
    }, {
      title: 'Let op de klankverandering',
      content: 'De klinker verandert vaak:\nzingen → gezongen (i → o)\nhelpen → geholpen (e → o)\nbinden → gebonden (i → o)'
    }]
  },
  'SWW-VD-ZONDER-GE': {
    familyId: 'SWW-VD-ZONDER-GE',
    defaultExplanation: 'Werkwoorden die beginnen met be-, ver-, ont-, her-, ge- of er- krijgen GEEN ge- in het voltooid deelwoord: betalen → betaald, vertellen → verteld.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Geen ge- bij:',
      content: 'be-: betalen → betaald\nver-: vertellen → verteld\nont-: ontdekken → ontdekt\nher-: herhalen → herhaald\nge-: gebruiken → gebruikt\ner-: erkennen → erkend'
    }, {
      title: 'Tip',
      content: 'Klinkt het raar met ge-? Dan hoort het er niet:\n"gebetaald" klinkt fout → betaald ✓\n"geverteld" klinkt fout → verteld ✓'
    }]
  },
  'SWW-VD-HULPWW': {
    familyId: 'SWW-VD-HULPWW',
    defaultExplanation: 'Bij het voltooid deelwoord gebruik je een hulpwerkwoord: hebben of zijn. "Ik heb gelopen" of "Ik ben gevallen". Beweging/verandering → zijn.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Hebben of zijn?',
      content: 'Zijn: bij verandering van plaats of toestand\nik ben gevallen, ik ben gegroeid, ik ben vertrokken\n\nHebben: bij de rest\nik heb gelopen, ik heb gegeten, ik heb gewerkt'
    }, {
      title: 'Ezelsbruggetje',
      content: 'Kun je "ergens naartoe" of "iets worden"?\n→ zijn\nAnders → hebben'
    }]
  },
  'SWW-LEEN-FRANS': {
    familyId: 'SWW-LEEN-FRANS',
    defaultExplanation: 'Franse leenwoorden als werkwoord: de stam bepaal je op dezelfde manier (-en eraf). Let op de v→f wissel: verven → verf.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Franse werkwoorden',
      content: 'Behandel ze als gewone Nederlandse werkwoorden:\nreserveren → reserveerde (r, niet in kofschip → -de)\nfourneren → fourneerde\nriskeren → riskeerde'
    }, {
      title: 'V/F-wissel',
      content: 'Let op bij verven:\nStam = verf (f in kofschip)\nMaar "verfte" klinkt raar → verfde\nDit is een uitzondering!'
    }]
  }
};

// S-5 (Support RC audit 2026): removed unused `SWW_SUPPORT_STATS` export.